'use client'

import { useState } from 'react'
import { X, Sparkles, ShoppingBag, Check, ArrowRight, Loader2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useProducts } from '@/hooks/useProducts'
import { useCartStore } from '@/stores/cart'
import { parseShoppingList, matchShoppingList, type ParsedItem, type ProductMatch } from '@/lib/ai-shopping'
import type { Product } from '@/types/product'

interface AIShoppingAssistantProps {
  open: boolean
  onClose: () => void
}

export default function AIShoppingAssistant({ open, onClose }: AIShoppingAssistantProps) {
  const [shoppingList, setShoppingList] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [step, setStep] = useState<'input' | 'review' | 'success'>('input')
  const [parsedItems, setParsedItems] = useState<ParsedItem[]>([])
  const [matches, setMatches] = useState<Map<string, ProductMatch[]>>(new Map())
  const [selectedProducts, setSelectedProducts] = useState<Map<string, { product: Product; quantity: number }>>(new Map())
  const [itemsAdded, setItemsAdded] = useState(0)

  const { data: productsData } = useProducts({})
  const products = productsData?.products || []
  const addItem = useCartStore(state => state.addItem)

  const sampleLists = [
    "2 kg rice\\n1 liter milk\\nEggs\\n500g chicken\\nBread\\n2 apples\\nTomatoes\\nOnions",
    "Basmati Rice 5kg\\nOlive Oil\\nWhole Wheat Bread\\nCheddar Cheese\\nFresh Milk 2 liter\\nBananas\\nSpinach",
    "3x Chocolate Cookies\\nVanilla Ice Cream\\nPotato Chips\\nCola 2 liter\\nMixed Nuts\\nGranola Bars"
  ]

  const handleProcess = async () => {
    if (!shoppingList.trim()) return

    setIsProcessing(true)
    
    // Simulate AI processing delay
    await new Promise(resolve => setTimeout(resolve, 1000))

    const items = parseShoppingList(shoppingList)
    const itemMatches = matchShoppingList(items, products)

    // Auto-select best matches with high confidence
    const autoSelected = new Map<string, { product: Product; quantity: number }>()
    items.forEach(item => {
      const matches = itemMatches.get(item.raw)
      if (matches && matches.length > 0 && matches[0].confidence > 0.7) {
        autoSelected.set(item.raw, {
          product: matches[0].product,
          quantity: item.quantity
        })
      }
    })

    setParsedItems(items)
    setMatches(itemMatches)
    setSelectedProducts(autoSelected)
    setStep('review')
    setIsProcessing(false)
  }

  const handleSelectProduct = (itemRaw: string, match: ProductMatch, quantity: number) => {
    const newSelected = new Map(selectedProducts)
    newSelected.set(itemRaw, { product: match.product, quantity })
    setSelectedProducts(newSelected)
  }

  const handleAddToCart = () => {
    let count = 0
    selectedProducts.forEach(({ product, quantity }) => {
      addItem({
        product_id: product.id,
        title: product.title,
        image: product.images[0]?.url || '',
        price: product.pricing.current_price,
        original_price: product.pricing.original_price,
        vendor_name: product.vendor.name,
      }, quantity)
      count++
    })
    setItemsAdded(count)
    setStep('success')
  }

  const handleReset = () => {
    setShoppingList('')
    setStep('input')
    setParsedItems([])
    setMatches(new Map())
    setSelectedProducts(new Map())
    setItemsAdded(0)
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">AI Shopping Assistant</h2>
              <p className="text-sm text-muted-foreground">
                Paste your shopping list and we'll find products for you
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 'input' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Your Shopping List
                </label>
                <textarea
                  className="w-full h-64 px-4 py-3 rounded-lg border border-input bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter your shopping list (one item per line)
Examples:
2 kg rice
Milk
3x eggs
Bread
500g chicken"
                  value={shoppingList}
                  onChange={(e) => setShoppingList(e.target.value)}
                />
              </div>

              <div>
                <p className="text-sm font-medium text-foreground mb-2">Try a sample list:</p>
                <div className="grid md:grid-cols-3 gap-2">
                  {sampleLists.map((sample, idx) => (
                    <button
                      key={idx}
                      onClick={() => setShoppingList(sample)}
                      className="text-left p-3 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors text-sm"
                    >
                      <p className="font-medium text-foreground mb-1">Sample {idx + 1}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {sample.split('\\n').slice(0, 2).join(', ')}...
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 'review' && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">
                  Found {parsedItems.length} items • {selectedProducts.size} matched
                </p>
              </div>

              {parsedItems.map((item) => {
                const itemMatches = matches.get(item.raw) || []
                const selected = selectedProducts.get(item.raw)

                return (
                  <div key={item.raw} className="border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-medium text-foreground">"{item.item}"</h3>
                        <p className="text-sm text-muted-foreground">Quantity: {item.quantity}</p>
                      </div>
                      {selected && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full">
                          <Check className="h-3 w-3" />
                          Selected
                        </span>
                      )}
                    </div>

                    {itemMatches.length === 0 ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <AlertCircle className="h-4 w-4" />
                        No matching products found
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {itemMatches.map((match, idx) => (
                          <button
                            key={match.product.id}
                            onClick={() => handleSelectProduct(item.raw, match, item.quantity)}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                              selected?.product.id === match.product.id
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/50 hover:bg-muted/50'
                            }`}
                          >
                            <img
                              src={match.product.images[0]?.url}
                              alt={match.product.title}
                              className="w-12 h-12 rounded object-cover"
                            />
                            <div className="flex-1 text-left">
                              <p className="font-medium text-foreground text-sm line-clamp-1">
                                {match.product.title}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                ৳{match.product.pricing.current_price}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs px-2 py-1 bg-muted rounded">
                                {Math.round(match.confidence * 100)}% match
                              </span>
                              {selected?.product.id === match.product.id && (
                                <Check className="h-5 w-5 text-primary" />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-10 w-10 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-2">
                {itemsAdded} Items Added to Cart!
              </h3>
              <p className="text-muted-foreground mb-6">
                Your selected products have been added to your cart
              </p>
              <div className="flex gap-3 justify-center">
                <Button onClick={handleReset} variant="outline">
                  Add More Items
                </Button>
                <Button onClick={handleClose}>
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  View Cart
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step !== 'success' && (
          <div className="flex items-center justify-between p-6 border-t border-border">
            {step === 'input' ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Powered by AI • Fast & Accurate
                </p>
                <Button
                  onClick={handleProcess}
                  disabled={!shoppingList.trim() || isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Find Products
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => setStep('input')} variant="outline">
                  Back
                </Button>
                <div className="flex gap-3">
                  <p className="text-sm text-muted-foreground self-center">
                    {selectedProducts.size} of {parsedItems.length} items selected
                  </p>
                  <Button
                    onClick={handleAddToCart}
                    disabled={selectedProducts.size === 0}
                  >
                    Add to Cart
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
